import { memo } from "react";
import { FileIcon, FolderIcon, LinkIcon, PaletteIcon, TypeIcon, XIcon } from "lucide-react";

import { cn } from "~/lib/utils";
import {
  COMPOSER_INLINE_CHIP_CLASS_NAME,
  COMPOSER_INLINE_CHIP_DISMISS_BUTTON_CLASS_NAME,
  COMPOSER_INLINE_CHIP_ICON_CLASS_NAME,
  COMPOSER_INLINE_CHIP_LABEL_CLASS_NAME,
  CONTEXT_INLINE_CHIP_TONE_CLASS_NAMES,
} from "../composerInlineChip";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

/**
 * What a Brain or Design operation's send has gathered, as removable chips.
 *
 * Attachments live in the prompt as inline references, but a text source and a
 * Delete target have no text to hang off: without a chip the send would carry
 * material the composer never showed, and there would be no way to take one
 * back short of clearing the draft.
 *
 * They wear the composer's own context-chip dress so a gathered source reads
 * like an attached one — same geometry, same label and kind slots — differing
 * only in the accent that says what it is.
 */
export interface BrainComposerChip {
  readonly id: string;
  readonly icon: "text" | "file" | "folder" | "link" | "palette";
  /** The chip's own text, truncated to fit. */
  readonly label: string;
  /** What the chip is, in the slot an attachment gives its size. */
  readonly kind: string;
  readonly title: string;
  readonly removeLabel: string;
  readonly onRemove: () => void;
}

const CHIP_ICON = {
  text: TypeIcon,
  file: FileIcon,
  folder: FolderIcon,
  link: LinkIcon,
  palette: PaletteIcon,
} as const;

const CHIP_TONE = {
  text: CONTEXT_INLINE_CHIP_TONE_CLASS_NAMES.file,
  /** Delete reads as a loss, so its chips carry the destructive accent. */
  delete: `${CONTEXT_INLINE_CHIP_TONE_CLASS_NAMES.file} [--context-chip-accent:oklch(0.62_0.16_16)]`,
  /** A Design System target, in the violet Design reads in. */
  design: `${CONTEXT_INLINE_CHIP_TONE_CLASS_NAMES.file} [--context-chip-accent:oklch(0.55_0.2_300)]`,
} as const;

export const BrainComposerChipRow = memo(function BrainComposerChipRow(props: {
  label: string;
  tone: keyof typeof CHIP_TONE;
  chips: ReadonlyArray<BrainComposerChip>;
}) {
  if (props.chips.length === 0) return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-1.5" aria-label={props.label}>
      {props.chips.map((chip) => {
        const Icon = CHIP_ICON[chip.icon];
        return (
          <Tooltip key={chip.id}>
            <TooltipTrigger
              render={
                <span
                  className={cn(
                    COMPOSER_INLINE_CHIP_CLASS_NAME,
                    CHIP_TONE[props.tone],
                    "text-[12px]",
                  )}
                  data-brain-composer-chip={props.tone}
                >
                  <Icon className={COMPOSER_INLINE_CHIP_ICON_CLASS_NAME} aria-hidden="true" />
                  <span className={cn(COMPOSER_INLINE_CHIP_LABEL_CLASS_NAME, "max-w-56")}>
                    {chip.label}
                  </span>
                  <span className="shrink-0 text-[10px] text-current">{chip.kind}</span>
                  <button
                    type="button"
                    aria-label={chip.removeLabel}
                    className={COMPOSER_INLINE_CHIP_DISMISS_BUTTON_CLASS_NAME}
                    onClick={chip.onRemove}
                  >
                    <XIcon className="size-[0.85em]" />
                  </button>
                </span>
              }
            />
            <TooltipPopup side="top" className="max-w-80 whitespace-pre-wrap leading-tight">
              {chip.title}
            </TooltipPopup>
          </Tooltip>
        );
      })}
    </div>
  );
});
